import React, { useEffect, useState, useCallback } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { X } from 'react-bootstrap-icons';
import QRScanner from '../../../../../../../../../components/common/QRCodes/QRScanner';
import { FaCamera, FaTicket, FaPercent, FaCheck, FaX, FaUserCheck } from 'react-icons/fa6';
import { validateClientCoupon } from '../../../../../../../../../crud/payments_api/client_coupons';
import './CouponSection.scss';
import { toast } from 'react-toastify';
import { getPermissionState } from '../../../../../../../../../utils/functions';
import MiniLoadingSpinner from '../../../../../../../../../components/Spinners/MiniLoadingSpinner';
import DiscountSection from './components/DiscountSection/DiscountSection';
import MembershipSection from './components/MembershipSection/MembershipSection';
import { updatePaymentReceiptSuggestedTip } from '../../../../../../../../../crud/payments_api/payment_receipts';

interface CouponSectionProps {
  receiptId: number;
  selections: any[];
  totalCuenta: number;
  couponCode: string;
  setCouponCode: (couponCode: string) => void;
  couponAmount: number;
  setCouponAmount: (couponAmount: number) => void;
  discountAmount: number;
  setDiscountAmount: (discountAmount: number) => void;
  receipts: any[];
  setReceipts: any;
  manualDiscountAmount?: number;
  setManualDiscountAmount?: (amount: number) => void;
  disabled?: boolean;
  setTipAmount?: (amount: number) => void;
}

const CouponSection: React.FC<CouponSectionProps> = ({
  receiptId,
  selections,
  totalCuenta,
  couponCode,
  setCouponCode,
  couponAmount,
  setCouponAmount,
  discountAmount,
  setDiscountAmount,
  receipts,
  setReceipts,
  manualDiscountAmount = 0,
  setManualDiscountAmount,
  disabled = false,
  setTipAmount,
}) => {
  const [applyCoupon, setApplyCoupon] = useState(false);
  const [showDiscountPanel, setShowDiscountPanel] = useState(false);
  const [showMembershipPanel, setShowMembershipPanel] = useState(false);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [qrInput, setQRInput] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasDiscountPermission, setHasDiscountPermission] = useState(false);

  // ========== PERMISSIONS ==========
  useEffect(() => {
    const hasPermission = getPermissionState(32);
    setHasDiscountPermission(hasPermission);
  }, []);

  // ========== HELPERS ==========
  const extractCouponCodeFromUrl = (input: string): string => {
    if (input.includes('ClientCouponLanding/')) {
      const parts = input.split('ClientCouponLanding/');
      if (parts.length > 1) {
        const code = parts[1].split('/')[0];
        return code;
      }
    }
    return input;
  };

  const handleQRCodeScanned = (scannedContent: string) => {
    const extractedCouponCode = extractCouponCodeFromUrl(scannedContent);
    setCouponCode(extractedCouponCode);
  };

  // ========== COUPON LOGIC ==========
  const validateCoupon = useCallback(async () => {
    if (loading) return;
    setLoading(true);

    const user = JSON.parse(localStorage.getItem('user') ?? '{}');
    const localId = user.local_id ? Number(user.local_id) : 0;

    try {
      const response: any = await validateClientCoupon(couponCode, localId, selections, receiptId);
      const couponSavings = response.data.map((e: any) => e.original_price - e.final_price);
      const savings = couponSavings.reduce((sum: number, discount: number) => sum + discount, 0);
      setCouponAmount(savings);
      setApplyCoupon(true);

      // Find the current receipt to get the current tip
      const currentReceipt = receipts.find((r: any) => r.receipt_id === receiptId);
      const currentTipAmount = currentReceipt?.suggested_tip ?? 0;
      
      // Recalculate tip proportionally after coupon is applied
      let newSuggestedTip = currentTipAmount;
      if (currentTipAmount > 0) {
        // Calculate old net base (before applying the new coupon)
        const oldNetBase = totalCuenta - couponAmount - discountAmount;
        // Calculate tip rate based on old net base
        const tipRate = oldNetBase > 0 ? currentTipAmount / oldNetBase : 0;
        // Calculate new net base (after applying the coupon)
        const newNetBase = totalCuenta - savings - discountAmount;
        // Apply the same tip rate to the new net base, or set to 0 if net becomes non-positive
        newSuggestedTip = newNetBase > 0 ? Math.round(newNetBase * tipRate) : 0;

        try {
          await updatePaymentReceiptSuggestedTip(receiptId, newSuggestedTip);
        } catch (error) {
          console.error('Error updating tip after coupon application:', error);
        }
      }

      const updatedReceipts = receipts.map((r: any) => {
        if (r.receipt_id === receiptId) {
          return { 
            ...r, 
            coupon_amount: savings, 
            coupon_code: couponCode,
            suggested_tip: newSuggestedTip,
          };
        }
        return r;
      });
      setReceipts(updatedReceipts);

      toast.success('Cupón validado');

      // Cierre de sección tras aplicar cupon
      setLoading(false);
      setApplyCoupon(false);       // oculta el panel de cupón
      setShowQRScanner(false);     // cierra el modal de QR si estaba abierto
      setQRInput(false);           // evita revalidaciones por efecto
    } catch (error: any) {
      if (error?.response?.data?.message && Array.isArray(error.response.data.message)) {
        if (error.response.data.message.includes('Coupon is not valid')) {
          toast.error('El cupón no es válido');
        } else {
          toast.error(error.response.data.message.join(' '));
        }
      } else if (error?.response?.data?.detail) {
        toast.error('Cupón inválido: ' + error.response.data.detail);
      } else {
        toast.error('Error validando cupón');
      }
      setCouponAmount(0);
      setApplyCoupon(false);
      setLoading(false);
    }
  }, [couponCode, selections, receiptId, setCouponAmount, receipts, setReceipts, loading, totalCuenta, couponAmount, discountAmount]);

  useEffect(() => {
    if (qrInput && couponCode) {
      validateCoupon();
    }
  }, [qrInput, couponCode, validateCoupon]);

  // ========== UI HANDLERS ==========
  const handleApplyCoupon = (val: boolean) => {
    setApplyCoupon(!val);
    setShowDiscountPanel(false);
    setShowMembershipPanel(false);
    if (!val) setCouponAmount(0);
  };

  const handleApplyDiscount = () => {
    setShowDiscountPanel(true);
    setApplyCoupon(false);
    setShowMembershipPanel(false);
  };

  const handleApplyMembership = () => {
    setShowMembershipPanel(true);
    setApplyCoupon(false);
    setShowDiscountPanel(false);
  };

  // ========== RENDER ==========
  return (
    <>
      <div className={`coupon-section ${disabled ? 'disabled' : ''}`}>
        <div className="coupon-buttons-container">
          <Button
            variant="none"
            onClick={() => handleApplyCoupon(applyCoupon)}
            className="icon-button"
            disabled={loading || disabled}
            title="Cupón"
          >
            <FaTicket />
            <span>Cupón</span>
          </Button>
          {hasDiscountPermission && (
            <Button
              variant="none"
              onClick={handleApplyDiscount}
              className="icon-button"
              disabled={loading || disabled}
              title="Descuento"
            >
              <FaPercent />
              <span>Descuento</span>
            </Button>
          )}
          <Button
            variant="none"
            onClick={handleApplyMembership}
            className="icon-button"
            disabled={loading || disabled}
            title="Membresía"
          >
            <FaUserCheck />
            <span>Membresía</span>
          </Button>
        </div>

        {applyCoupon && (
          <div className="coupon-validation">
            <div className="coupon-input-row">
              <Form.Control
                type="text"
                value={couponCode}
                placeholder="Ingrese código del cupón..."
                className="coupon-input"
                onChange={(e) => setCouponCode(e.target.value)}
                disabled={loading}
              />
              <Button
                variant="none"
                className="qr-scan-button"
                onClick={() => setShowQRScanner(true)}
                disabled={loading}
                title="Escanear código QR"
              >
                <FaCamera />
              </Button>
            </div>
            
            <div className="coupon-actions">
              <Button
                variant="none"
                className="action-button secondary"
                onClick={() => handleApplyCoupon(applyCoupon)}
                disabled={loading}
              >
                <FaX />
                Cancelar
              </Button>
              <Button
                variant="none"
                className="action-button primary"
                onClick={validateCoupon}
                disabled={loading || !couponCode.trim()}
              >
                <FaCheck />
                Validar cupón
              </Button>
            </div>
          </div>
        )}

        {showDiscountPanel && (
          <DiscountSection
            receiptId={receiptId}
            totalCuenta={totalCuenta}
            discountAmount={discountAmount}
            setDiscountAmount={setDiscountAmount}
            receipts={receipts}
            setReceipts={setReceipts}
            handleClose={() => setShowDiscountPanel(false)} // el hijo ya cierra al aplicar
            manualDiscountAmount={manualDiscountAmount}
            setManualDiscountAmount={setManualDiscountAmount}
            setTipAmount={setTipAmount}
          />
        )}

        {showMembershipPanel && (
          <MembershipSection
            receiptId={receiptId}
            receipts={receipts}
            setReceipts={setReceipts}
            onMembershipApplied={() => setShowMembershipPanel(false)}
          />
        )}
      </div>

      <Modal 
        show={showQRScanner} 
        onHide={() => setShowQRScanner(false)}
        centered
        size="lg"
      >
        <Modal.Header>
          <div className="form-modal-title-container">
            <Modal.Title className="form-modal-title">Scanea el QR</Modal.Title>
          </div>
          <Button variant="none" className="modal-close-button" onClick={() => setShowQRScanner(false)}>
            <X size={24} />
          </Button>
        </Modal.Header>
        <Modal.Body>
          <QRScanner
            isOpen={showQRScanner}
            setIsOpen={setShowQRScanner}
            setQRContent={handleQRCodeScanned}
            setQrInput={setQRInput}
          />
        </Modal.Body>
      </Modal>

      {loading && <MiniLoadingSpinner left="50%" />}
    </>
  );
};

export default CouponSection;
